import { partition } from 'lodash'
import { useMemo } from 'react'

import { Dayjs, dayjsEn } from '@bemlo/date'
import { getRuleViolationsMap } from '@bemlo/schedule'
import { Maybe } from '@bemlo/utils'

import { HoverCard } from '../../../../elements'
import { useTranslations } from '../../../../translate'
import { byCellFilter } from '../by-filter'
import { AvailabilityCell, Cell } from '../components/Cell'
import { CellShiftsColumn } from '../components/CellShiftsColumn'
import { DraftShifts } from '../components/DraftShifts'
import { NewShift } from '../components/NewShift'
import { ResourceTitle } from '../components/ResourceTitle'
import { Row } from '../components/Row'
import { ShiftError } from '../components/ShiftError'
import { ScheduleSchedulingRules, WithResourceHoverCard } from '../types'
import { useEditShifts } from '../useEditShifts'
import { useSchedule } from '../useSchedule'
import { getShiftsInWeek } from '../utils/get-shifts-in-week'

type DraftResourceProps<T extends object = object> =
  WithResourceHoverCard<T> & {
    rowKey: number
    homeUnitId: string
    schedulingRules?: ScheduleSchedulingRules
    url?: string
    onClickNewShift?: (date: string) => void
    onClickShift?: (shift: string) => void
    multipleShifts?: boolean
    requiredDays?: number[]
    assignedResource?: { fullName: string; id: string } | undefined | null
    contract: Maybe<{
      startsAt: number | null
      endsAt: number | null
    }>
  }

export const DraftResource = <T extends object>(
  props: DraftResourceProps<T>,
) => {
  const t = useTranslations()

  const {
    days,
    currentWeek,
    shifts: allShifts,
    availability: allAvailability,
    shiftPopover,
  } = useSchedule()

  const { enableEditing, onDeleteDraftShift } = useEditShifts(undefined)

  const [shifts, shiftsInWeek, dayErrors, assignedAvailability, isEmployed] =
    useMemo(() => {
      const shifts = allShifts.filter(byCellFilter(props))
      const shiftsInWeek = currentWeek
        ? getShiftsInWeek(currentWeek, shifts)
        : shifts

      const assignedAvailability = props.assignedResource
        ? allAvailability.filter(
            ({ candidateId }) => candidateId === props.assignedResource?.id,
          )
        : []

      const dayErrors = props.schedulingRules
        ? getRuleViolationsMap(
            props.schedulingRules.ruleSets,
            shifts,
            props.schedulingRules.rules,
            props.schedulingRules.resource,
          )
        : {}

      const unixContract = props.contract
      const contract = {
        startsAt: unixContract?.startsAt
          ? dayjsEn.unix(unixContract.startsAt)
          : null,
        endsAt: unixContract?.endsAt ? dayjsEn.unix(unixContract.endsAt) : null,
      }

      // Function to see if the shifts is within the contract
      const isEmployed = (date: Dayjs) => {
        if (!contract.startsAt || !contract.endsAt) return true
        if (contract.startsAt && date.isBefore(contract.startsAt)) return false
        if (contract.endsAt && date.isAfter(contract.endsAt)) return false
        return true
      }

      return [shifts, shiftsInWeek, dayErrors, assignedAvailability, isEmployed]
    }, [allShifts, props, currentWeek])

  const name =
    props.assignedResource?.fullName ?? t.shifts.row_n(props.rowKey + 1)
  const title = (
    <ResourceTitle
      title={name}
      shifts={shiftsInWeek}
      bold={!!props.assignedResource}
      url={props.url}
    />
  )

  const HoverCardContent = props.hoverCard

  return (
    <Row>
      <Cell isTitle rowSpan={1}>
        {HoverCardContent ? (
          <HoverCard
            side="right"
            trigger={<HoverCard.Trigger>{title}</HoverCard.Trigger>}
          >
            <HoverCardContent
              candidateId={null}
              name={name}
              shifts={shifts}
              availability={assignedAvailability}
              {...(props.hoverCardProps || ({} as T))}
            />
          </HoverCard>
        ) : (
          title
        )}
      </Cell>
      {days.map((day, i) => {
        const [draftShifts, shiftsOnDay] = partition(
          shifts.filter((shift) => shift.date === day.dateISO),
          ({ status }) => status === 'DRAFT',
        )
        const hasShifts = !!draftShifts.length || !!shiftsOnDay.length
        const errorsOnDay =
          dayErrors[day.dateISO]?.map((error) => ({
            error: t.$schedulingRuleViolation(error),
            isWarning: error.severity === 'warning',
          })) ?? []
        const availabilityOnDay = assignedAvailability.filter(
          ({ date }) => date === day.dateISO,
        )
        const isRequired = props.requiredDays?.includes(i)

        return (
          <AvailabilityCell
            key={day.dateISO}
            disabled={!day.withinRange || !isEmployed(day.date)}
            weekend={day.isWeekend}
            availability={availabilityOnDay}
            isOccupied={hasShifts}
            isRequired={isRequired}
          >
            <CellShiftsColumn>
              {shiftsOnDay.map((shift) => {
                const isArchived = shift.status === 'ARCHIVED'
                const isUrgent = shift.isUrgent && !isArchived

                if (!shift) return null

                const isAtOtherUnit = shift.unitId !== props.homeUnitId

                return (
                  <NewShift
                    key={shift.id}
                    shift={shift}
                    interestedCandidates={shift.interestedCandidates}
                    isAtOtherUnit={isAtOtherUnit}
                    unitName={
                      isAtOtherUnit
                        ? shift.unitName
                        : shift.scheduleResourceName
                    }
                    popoverContent={shiftPopover}
                    shiftIds={[shift.id]}
                    status={shift.status}
                    isUrgent={isUrgent}
                    unselectable={isArchived}
                    error={<ShiftError errors={errorsOnDay} />}
                  />
                )
              })}
              {enableEditing && day.withinRange && (
                <DraftShifts
                  showBadges
                  shifts={draftShifts}
                  onClickNewShift={() => props.onClickNewShift?.(day.dateISO)}
                  onClickShift={props.onClickShift}
                  onDeleteDraftShift={onDeleteDraftShift}
                />
              )}
            </CellShiftsColumn>
          </AvailabilityCell>
        )
      })}
    </Row>
  )
}
